/**
 * stats.js — the figures on each row of the deal list, and how they rank
 * ======================================================================
 * Brian, 21 September 2026, pointing at a list whose rows read "-9.2M" and
 * nothing else: "make this part of deal reports actually mean something like
 * quick stats, gross $, MOIC, IRR, exposure remaining."
 *
 * They were first taken from the cash waterfall engine, and his answer was
 * "its missing so much info, thats not good. you should be able to generate
 * those numbers". That engine reads cash off tax lots, and a deal never
 * rebuilt into lots returned a confident zero for a deal the ledger says
 * deployed millions. They now come from `reports/services/deal_stats.py`, on
 * the same payload as the series, read off the general ledger — the record
 * the curve is drawn from, which has every deal on this page by construction.
 * The block is on each deal as `stats` and carries its own currency: a
 * single-currency deal reads in its own, because MOIC and IRR are unitless and
 * krona over krona needs no fixing.
 *
 * Two states, and only the first prints a number:
 *
 *   measured    every figure is there, in `stats.currency`
 *   withheld    the deal's cash spans currencies and a rate one of its dates
 *               needs is not loaded, so there is no denominator. It reads n/a
 *               with the reason, never 0.00 (CLAUDE.md 0.30).
 *
 * Invested is capital, not every outflow. Brian, 27 October 2026, on XCF
 * Global's convertible note reading 7.8M: "invested means true capital
 * deployed in convertible notes, prefs, warrant exercises, etc etc. ELOCs do
 * not use capital" (D-285). Counsel is in the cost basis; equity-line draws,
 * market buys around a position and an ACE deal's same-day sales come off Cash
 * back instead, so P&L is where it was and MOIC is (capital + P&L) ÷ capital.
 * An equity line has no capital and no MOIC.
 *
 * Usability review of 23 October 2026, item 21, added Cash back to the four
 * Brian kept ("total invested, net profit/loss, MOIC, IRR"): the list became a
 * plain table, and invested beside cash back is the pair a reader subtracts.
 */
import theme from "../../components/theme";
import { DASH, multiple, pct, toNumber } from "../../components/format";
import { compactFigure, dealMoney } from "./figures";

export const STAT_COLUMNS = [
  {
    key: "capital_deployed",
    label: "Invested",
    title: "Capital put into the deal: notes, preferred, warrant exercises, PPLNs, shares subscribed, and the counsel paid at their closing. Equity-line draws, market buys around a position, what an ACE deal's same-day sale paid for, later legal and any other cash out are not capital; they come off cash back.",
  },
  {
    key: "cash_returned_net",
    label: "Cash back",
    title: "Everything that has come back — shares sold, notes repaid, preferred redeemed, interest and fees — less what was paid out of it that is not capital: equity-line draws, market buys, and the part of an ACE deal's units its same-day sale paid for.",
  },
  {
    key: "net_profit",
    label: "P&L",
    title: "Cash back plus what is still held, less everything it took. On a deal carried at cost with nothing returned this is nil, which reads “nothing recognised yet” rather than “flat”.",
  },
  {
    key: "moic",
    label: "MOIC",
    title: "(cash back + what is still held) ÷ invested. The same two figures P&L subtracts, so the two cannot disagree. A deal with no capital in, an equity line, has none.",
  },
  {
    key: "irr_pct",
    label: "IRR",
    title: "Annualised over this deal's own dated ledger flows, with what is still held as a flow today. Blank where it does not converge.",
  },
];

const MONEY_KEYS = ["capital_deployed", "cash_returned_net", "net_profit"];

// One figure, in the spelling its state earns.
export function statText(stats, column) {
  if (!stats) return DASH;
  if (stats.measured === false) return "n/a";
  const value = stats[column.key];
  if (toNumber(value) === null) return DASH;
  if (column.key === "moic") return multiple(value);
  if (column.key === "irr_pct") return pct(value, 0);
  return compactFigure(value);
}

// The exact figure behind a compact one, for the cell's title.
export function statExact(stats, column) {
  if (!stats || stats.measured === false || !MONEY_KEYS.includes(column.key)) return null;
  const value = stats[column.key];
  return toNumber(value) === null ? null : dealMoney(value, stats.currency || "USD");
}

// P&L, MOIC and IRR carry a judgement. Invested and cash back do not: they
// are sizes, and a colour on them would say a big deal is a good one.
export function statTone(stats, column) {
  if (!stats || stats.measured === false) return theme.textDisabled;
  const value = toNumber(stats[column.key]);
  if (value === null) return theme.textDisabled;
  // Nil is neither, and on this book it is the commonest honest answer — a
  // deal carried at cost has recognised nothing.
  if (column.key === "net_profit") {
    if (value === 0) return theme.textSecondary;
    return value > 0 ? theme.success : theme.danger;
  }
  if (column.key === "moic") return value >= 1 ? theme.success : theme.danger;
  if (column.key === "irr_pct") return value >= 0 ? theme.success : theme.danger;
  return theme.textSecondary;
}

const BREAK_NOTE =
  "This deal's investment accounts net below zero — a sale or redemption posted " +
  "against a position that was never booked in. Nothing can be counted as held, " +
  "so this counts the cash only, and is a floor until the accounts are corrected.";

// What a reader should be told about one figure when they stop on it.
export function statTitle(stats, column) {
  if (!stats) return "No figures for this deal";
  if (stats.measured === false) return stats.withheld_because || "withheld";
  if (column.key === "moic" && toNumber(stats.moic) === null && stats.moic_reads_as) {
    return `No MOIC: ${stats.moic_reads_as}.`;
  }
  if (column.key === "net_profit" || column.key === "moic") {
    if (stats.carrying_is_negative) return BREAK_NOTE;
    return `${column.title} What is still held here is ${
      stats.value_basis === "marked"
        ? "marked: carrying value plus the mark on the open lots that have one."
        : "carried at cost — no open lot has a market price, which is a ruling for a note, a preferred, a PPLN or an LP interest and a gap for a listed equity."
    }`;
  }
  if (column.key === "irr_pct") {
    if (stats.carrying_is_negative) return BREAK_NOTE;
    return stats.irr_reads_as || column.title;
  }
  return column.title;
}

/* Ranking the list.

   The question somebody arrives with is nearly always a ranking one — which
   of these made money, which is the biggest — so every heading sorts, and the
   third click on one puts the list back into the page's own order (the most
   cash tied up first).

   What a row sorts on is not always what it prints, and a figure the code
   cannot stand behind may not be ranked against figures it can:

     n/a     the deal's cash spans currencies with a fixing missing, so there
             is no figure at all. Sorting it as zero is the same defect as
             printing it as zero (CLAUDE.md 0.30) — worse, because a reader
             who sorted by P&L is reading position as meaning.
     break   the investment accounts net below zero (0.26), so nothing can be
             held and P&L, MOIC and IRR count the cash alone. Those are
             floors, and a floor ranked against a figure sorts wrongly while
             looking exactly as plausible as the right answer.

   A break does not reach Invested or Cash back — they are cash out of and
   into the ledger, true whatever the investment accounts do — so a break row
   ranks on those like any other (0.33: test which figures the defect reaches).
   Either way a row with nothing to rank on sits last, in both directions.
*/

// The columns whose figure counts what is still held, and which a negative
// carrying value therefore turns into a floor.
export const VALUE_BASIS_COLUMNS = ["net_profit", "moic", "irr_pct"];

export function sortValue(stats, key) {
  if (!stats || stats.measured === false) return null;
  if (stats.carrying_is_negative && VALUE_BASIS_COLUMNS.includes(key)) return null;
  return toNumber(stats[key]);
}

// The columns that sort on words, A to Z on the first click. A figure sorts
// biggest first on the first click — the largest invested, the best P&L.
export const TEXT_SORTS = {
  name: (d) => d.deal?.name,
  strategy: (d) => d.deal?.strategy,
  status: (d) => d.deal?.status,
  currency: (d) => d.stats?.currency,
};

export function nextSort(current, key) {
  const first = TEXT_SORTS[key] ? "asc" : "desc";
  if (!current || current.key !== key) return { key, dir: first };
  if (current.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

const textValue = (deal, key) => {
  const raw = TEXT_SORTS[key](deal);
  const text = (raw || "").trim();
  return text ? text.toLowerCase() : null;
};

const rankValue = (deal, key) =>
  TEXT_SORTS[key] ? textValue(deal, key) : sortValue(deal.stats, key);

export function sortDeals(deals, sort) {
  if (!sort) return deals;
  const sign = sort.dir === "asc" ? 1 : -1;
  // Array.prototype.sort is stable, so deals that tie keep the order they
  // arrived in rather than shuffling on a click.
  return [...deals].sort((a, b) => {
    const x = rankValue(a, sort.key);
    const y = rankValue(b, sort.key);
    // A row with nothing to rank on goes last whichever way the column
    // points. Reversing the sort must not promote it to the top.
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    if (x === y) return 0;
    return x < y ? -sign : sign;
  });
}

// How many of the rows on screen have nothing to rank on this column, so the
// footer can say it. A reader who cannot see why three rows are at the bottom
// reads them as the worst three.
export function unrankedCount(deals, sort) {
  if (!sort) return 0;
  return deals.filter((d) => rankValue(d, sort.key) === null).length;
}
